import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Brain, Lock, Unlock, Check, Archive, Trash2, Plus, ChevronDown,
  Sparkles, TrendingUp, History as HistoryIcon, Layers, Pencil,
} from "lucide-react";
import "./ParishMemory.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ["Worship", "Music", "Clergy", "Liturgical Customs", "Seasonal Traditions",
  "Planning Preferences", "Facilities", "Volunteers", "Parish Notes"];

// Which parts of Liturgical Intelligence™ each category of memory influences.
const INFLUENCES = {
  "Worship": ["SundayBuilder™", "Parish Intelligence™", "Musical Intelligence™", "Annual Planning", "Holy Week Planning"],
  "Music": ["Musical Intelligence™", "SundayBuilder™", "Holy Week Planning", "Annual Planning"],
  "Clergy": ["SundayBuilder™", "Parish Intelligence™", "Formation Intelligence™ (future)"],
  "Liturgical Customs": ["SundayBuilder™", "Parish Intelligence™", "Holy Week Planning", "Annual Planning"],
  "Seasonal Traditions": ["SundayBuilder™", "Christmas Cycle", "Holy Week Planning", "Annual Planning"],
  "Planning Preferences": ["SundayBuilder™", "Annual Planning", "Parish Intelligence™"],
  "Facilities": ["SundayBuilder™", "Parish Intelligence™"],
  "Volunteers": ["Parish Intelligence™", "SundayBuilder™", "Formation Intelligence™ (future)"],
  "Parish Notes": ["Parish Intelligence™", "SundayBuilder™"],
};
const influencesFor = (cat) => INFLUENCES[cat] || INFLUENCES["Parish Notes"];

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");

function StatusBadge({ m }) {
  const label = m.locked ? "Locked" : m.status === "suggested" ? "Suggested" : m.status === "archived" ? "Archived" : "Accepted";
  const cls = m.locked ? "locked" : m.status;
  return <span className={`pm-badge ${cls}`} data-testid={`mem-status-${m.id}`}>{label}</span>;
}

function MemoryCard({ m, onAction, onDelete, onEdit }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ title: m.title, value: m.value, category: m.category });
  const sources = m.sources || [];
  const trend = m.prev_confidence != null && m.prev_confidence !== m.confidence;

  const saveEdit = async () => { await onEdit(m.id, draft); setEditing(false); };

  return (
    <div className={`pm-card${m.locked ? " is-locked" : ""}${m.needs_review ? " needs-review" : ""}`} data-testid={`mem-card-${m.id}`}>
      {m.needs_review && (
        <div className="pm-review-flag" data-testid={`mem-review-flag-${m.id}`}>
          ⚑ Your Liturgical Library™ has changed — please review whether this entry is still accurate.
        </div>
      )}
      <div className="pm-card-top">
        <div className="pm-card-main">
          <div className="pm-card-title">{m.title}</div>
          <div className="pm-card-meta">
            <span>{m.category}</span>
            <span className="pm-dot">·</span>
            <span>Confidence {Math.round((m.confidence || 0) * 100)}%</span>
            <span className="pm-dot">·</span>
            <span>Observed {m.times_observed || 1}×</span>
            <span className="pm-dot">·</span>
            <span>Last confirmed {fmtDate(m.last_confirmed || m.last_updated)}</span>
          </div>
        </div>
        <StatusBadge m={m} />
      </div>

      <div className="pm-card-actions">
        <button className="pm-mini" onClick={() => setOpen(!open)} data-testid={`mem-details-${m.id}`}>
          View Details <ChevronDown size={14} className={open ? "up" : ""} />
        </button>
        {m.status === "suggested" && (
          <button className="pm-mini accept" onClick={() => onAction(m.id, "accept")} data-testid={`mem-accept-${m.id}`}>
            <Check size={14} /> Accept
          </button>
        )}
        <button className="pm-mini" onClick={() => onAction(m.id, m.locked ? "unlock" : "lock")} data-testid={`mem-lock-${m.id}`}>
          {m.locked ? <><Unlock size={14} /> Unlock</> : <><Lock size={14} /> Lock</>}
        </button>
        <button className="pm-mini" onClick={() => { setEditing(!editing); setOpen(true); }} data-testid={`mem-edit-${m.id}`}>
          <Pencil size={14} /> Edit
        </button>
      </div>

      {open && (
        <div className="pm-detail" data-testid={`mem-detail-body-${m.id}`}>
          {editing ? (
            <div className="pm-edit">
              <label>Title</label>
              <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} data-testid={`mem-edit-title-${m.id}`} />
              <label>Value</label>
              <textarea value={draft.value} onChange={(e) => setDraft({ ...draft, value: e.target.value })} data-testid={`mem-edit-value-${m.id}`} />
              <label>Category</label>
              <select value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} data-testid={`mem-edit-cat-${m.id}`}>
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
              <div className="pm-edit-actions">
                <button className="pm-mini accept" onClick={saveEdit} data-testid={`mem-save-${m.id}`}><Check size={14} /> Save</button>
                <button className="pm-mini" onClick={() => setEditing(false)}>Cancel</button>
              </div>
            </div>
          ) : (
            <>
              <div className="pm-d-block">
                <h5>Current Value</h5>
                <p className="pm-value">{m.value}</p>
              </div>

              <div className="pm-d-block">
                <h5><Sparkles size={14} /> Why Ordo AI™ Learned This</h5>
                <p>
                  {m.source === "manual"
                    ? "Entered directly by your parish."
                    : `Observed across ${m.times_observed || 1} generation(s). ${m.status === "accepted" ? "Accepted by the parish." : "Proposed by Ordo AI™ — awaiting your review."} ${m.locked ? "Locked as a permanent parish truth." : "No conflicting preferences detected."}`}
                </p>
              </div>

              {sources.length > 0 && (
                <div className="pm-d-block">
                  <h5><Layers size={14} /> Source History</h5>
                  <ul className="pm-sources">
                    {sources.slice().reverse().map((s, i) => {
                      const clickable = s.ref_id && s.type !== "manual";
                      return (
                        <li key={i}>
                          <span className="pm-src-type">{s.type}</span>
                          {clickable ? (
                            <button className="pm-src-link" onClick={() => navigate("/reports")} data-testid={`mem-source-${m.id}-${i}`}>
                              {s.title} <em>{fmtDate(s.at)}</em>
                            </button>
                          ) : (
                            <span>{s.title} <em>{fmtDate(s.at)}</em></span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              <div className="pm-d-row">
                <div className="pm-d-block">
                  <h5><TrendingUp size={14} /> Confidence Trend</h5>
                  <div className="pm-trend">
                    <div><b>{Math.round((m.confidence || 0) * 100)}%</b><span>Current</span></div>
                    {trend && <div className="prev"><b>{Math.round((m.prev_confidence || 0) * 100)}%</b><span>Previous</span></div>}
                  </div>
                  {trend && <p className="pm-hint">Confidence rose as this preference recurred.</p>}
                </div>
                <div className="pm-d-block">
                  <h5><HistoryIcon size={14} /> Observation History</h5>
                  <ul className="pm-obs">
                    <li>Originally learned <b>{fmtDate(m.created_at)}</b></li>
                    <li>Times observed <b>{m.times_observed || 1}</b></li>
                    <li>Last confirmed <b>{fmtDate(m.last_confirmed || m.last_updated)}</b></li>
                  </ul>
                </div>
              </div>

              <div className="pm-d-block">
                <h5><Layers size={14} /> Influences — this memory powers</h5>
                <ul className="pm-impact" data-testid={`mem-influences-${m.id}`}>
                  {influencesFor(m.category).map((x) => {
                    const future = x.includes("(future)");
                    return <li key={x} className={future ? "future" : ""}><Check size={13} /> {x}</li>;
                  })}
                </ul>
              </div>

              <div className="pm-d-actions">
                {sources.some((s) => s.ref_id && s.type !== "manual") && (
                  <button className="pm-mini" onClick={() => navigate("/reports")} data-testid={`mem-viewsource-${m.id}`}>View Source Report</button>
                )}
                {m.status !== "archived"
                  ? <button className="pm-mini" onClick={() => onAction(m.id, "archive")} data-testid={`mem-archive-${m.id}`}><Archive size={14} /> Archive</button>
                  : <button className="pm-mini accept" onClick={() => onAction(m.id, "accept")} data-testid={`mem-restore-${m.id}`}><Check size={14} /> Restore</button>}
                <button className="pm-mini danger" onClick={() => onDelete(m.id)} data-testid={`mem-delete-${m.id}`}><Trash2 size={14} /> Delete</button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function ParishMemory() {
  const navigate = useNavigate();
  const [subscribed, setSubscribed] = useState(true);
  const [memories, setMemories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ category: "Worship", title: "", value: "", locked: false });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/parish/memory`);
      setSubscribed(data.subscribed);
      setMemories(data.memories || []);
    } catch (e) { console.warn("Parish Memory load failed:", e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onAction = async (id, action) => {
    try { await axios.patch(`${API}/parish/memory/${id}`, { action }); load(); }
    catch (e) { alert(e.response?.data?.detail?.message || "Could not update memory."); }
  };
  const onEdit = async (id, body) => {
    try { await axios.patch(`${API}/parish/memory/${id}`, body); load(); }
    catch (e) { alert("Could not save changes."); }
  };
  const onDelete = async (id) => {
    if (!window.confirm("Permanently delete this memory?")) return;
    try { await axios.delete(`${API}/parish/memory/${id}`); load(); }
    catch (e) { console.warn("Memory delete failed:", e); alert("Could not delete this memory."); }
  };
  const create = async () => {
    if (!form.title || !form.value) { alert("Add a title and value."); return; }
    try { await axios.post(`${API}/parish/memory`, form); setForm({ category: "Worship", title: "", value: "", locked: false }); setAdding(false); load(); }
    catch (e) { alert(e.response?.data?.detail?.message || "Could not add memory."); }
  };

  const visible = memories.filter((m) => m.status !== "dismissed");
  const suggested = visible.filter((m) => m.status === "suggested");
  const byCat = CATEGORIES.map((c) => [c, visible.filter((m) => m.category === c && m.status !== "suggested")])
    .filter(([, arr]) => arr.length);
  const locked = visible.filter((m) => m.locked).length;
  const needsReview = visible.filter((m) => m.needs_review);
  const avg = visible.length ? Math.round(visible.reduce((a, m) => a + (m.confidence || 0), 0) / visible.length * 100) : 0;

  return (
    <div className="pm" data-testid="parish-memory-page">
      <div className="pm-top">
        <div className="pm-brand"><Brain size={20} /> Parish <span>Memory™</span></div>
        <button className="pm-back" onClick={() => navigate("/dashboard")} data-testid="pm-back">← Dashboard</button>
      </div>

      {!subscribed ? (
        <div className="pm-upsell" data-testid="pm-upsell">
          <Brain size={40} />
          <h1>Your parish's living institutional memory</h1>
          <p>Ordo AI™ already learns how your parish worships from every generation. With <b>Parish Pro</b>, that intelligence is remembered permanently — confidence-scored, editable, and lockable — and automatically improves every future worship plan and report.</p>
          <ul className="pm-upsell-list">
            <li><Check size={16} /> Permanent, growing parish memory</li>
            <li><Check size={16} /> Accept, edit, lock & archive what Ordo AI™ learns</li>
            <li><Check size={16} /> Explainable recommendations across every module</li>
          </ul>
          <button className="pm-cta" onClick={() => navigate("/account")} data-testid="pm-upgrade-btn">Upgrade to Parish Pro</button>
        </div>
      ) : loading ? (
        <div className="pm-loading"><div className="pm-spin" /><p>Loading parish memory…</p></div>
      ) : (
        <div className="pm-body">
          <div className="pm-hero">
            <div>
              <div className="pm-eyebrow">Dynamic institutional intelligence</div>
              <h1>Parish Memory™</h1>
              <p className="pm-sub">What Ordo AI™ has learned about how your parish worships — continuously evolving and used in every recommendation.</p>
            </div>
            <div className="pm-stats" data-testid="pm-stats">
              <div className="pm-stat"><b>{visible.length}</b><span>Memories</span></div>
              <div className="pm-stat"><b>{locked}</b><span>Locked</span></div>
              <div className="pm-stat"><b>{avg}%</b><span>Avg Confidence</span></div>
            </div>
          </div>

          <div className="pm-addbar">
            <button className="pm-add" onClick={() => setAdding(!adding)} data-testid="pm-add-btn"><Plus size={15} /> Add Memory</button>
          </div>

          {needsReview.length > 0 && (
            <div className="pm-review-banner" data-testid="pm-review-banner">
              <span>Your Liturgical Library™ has changed. {needsReview.length} older Parish Memory™ {needsReview.length === 1 ? "entry" : "entries"} about worship resources may need review — your locked entries are untouched until you decide.</span>
            </div>
          )}

          {adding && (
            <div className="pm-addform" data-testid="pm-add-form">
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="pm-add-cat">
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
              <input placeholder="Title (e.g. Preferred Eucharistic Rite)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="pm-add-title" />
              <input placeholder="Value (e.g. Rite II)" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} data-testid="pm-add-value" />
              <label className="pm-lockchk"><input type="checkbox" checked={form.locked} onChange={(e) => setForm({ ...form, locked: e.target.checked })} data-testid="pm-add-lock" /> Lock</label>
              <button className="pm-mini accept" onClick={create} data-testid="pm-add-save">Save</button>
            </div>
          )}

          {visible.length === 0 && (
            <div className="pm-empty" data-testid="pm-empty">
              No memories yet. Generate a worship plan or Parish Intelligence report and Ordo AI™ will start proposing what to remember.
            </div>
          )}

          {suggested.length > 0 && (
            <section className="pm-section" data-testid="pm-section-suggested">
              <h2 className="pm-sec-h"><Sparkles size={16} /> Suggested by Ordo AI™ <span className="pm-count">{suggested.length}</span></h2>
              <p className="pm-sec-sub">Review and accept what Ordo AI™ proposed from recent generations.</p>
              {suggested.map((m) => <MemoryCard key={m.id} m={m} onAction={onAction} onDelete={onDelete} onEdit={onEdit} />)}
            </section>
          )}

          {byCat.map(([cat, arr]) => (
            <section className="pm-section" key={cat} data-testid={`pm-section-${cat.replace(/\s+/g, "-").toLowerCase()}`}>
              <h2 className="pm-sec-h">{cat} <span className="pm-count">{arr.length}</span></h2>
              {arr.map((m) => <MemoryCard key={m.id} m={m} onAction={onAction} onDelete={onDelete} onEdit={onEdit} />)}
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
